/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  NavigationMenuContent,
  NavigationMenuContentLink,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculatorSimple,
  faCalendar,
  faCalendarDays,
  faClock,
  faEllipsis,
  faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { Link, useLocation } from 'react-router-dom';
import { faChartMixed } from '@fortawesome/pro-solid-svg-icons';
import { ManagerProfile, useProfile } from '@/hooks';
import { buildLink } from '@/utils';
import { Managerrechte } from '@/gql/graphql';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { Standort } from '@/features/standortContext/entities/standort';
import { isMenuItemActive } from '@/domain/navigation/features/navigation/utils/helpers';

// Hier würde ich die Linter Regeln auch Disabeln, da ich nicht wüsste wie man das Zusammenbauen der Menuitems anders handeln könnte
export function useMenuItems() {
  const { t } = useTranslation();
  const profile = useProfile();
  const location = useLocation();
  const manager = profile.manager;
  const { currentStandort } = useStandortContext();
  const menuItems = [];

  if (manager.hasControllingRecht()) {
    menuItems.push(
      createControllingDropdown(manager, t, location.pathname, currentStandort),
    );
  }

  if (manager.rechte.mitarbeiter.erlaubt) {
    const isActive = isMenuItemActive('mitarbeiter', location.pathname);
    menuItems.push(
      <NavigationMenuItem key="employee" data-testid="menu-item-employee">
        <NavigationMenuLink
          active={isActive}
          href={buildLink('mitarbeiterListe')}>
          <FontAwesomeIcon icon={faUserGroup} />
          <span className="hidden xl:inline">{t('entities.employee_one')}</span>
        </NavigationMenuLink>
      </NavigationMenuItem>,
    );
  }

  if (manager.rechte.kalender) {
    const isActive = isMenuItemActive('kalender', location.pathname);
    menuItems.push(
      <NavigationMenuItem key="kalender">
        <NavigationMenuLink active={isActive} href={buildLink('kalender')}>
          <FontAwesomeIcon icon={faCalendar} />
          <span className="hidden xl:inline">{t('entities.calendar')}</span>
        </NavigationMenuLink>
      </NavigationMenuItem>,
    );
  }

  if (manager.rechte.arbeitszeit.erlaubt) {
    const isActive = isMenuItemActive('arbeitszeiten', location.pathname);
    menuItems.push(
      <NavigationMenuItem key="arbeitszeit">
        <NavigationMenuLink active={isActive} href={buildLink('arbeitszeiten')}>
          <FontAwesomeIcon icon={faClock} />
          <span className="hidden xl:inline">
            {t('entities.workingHour_other')}
          </span>
        </NavigationMenuLink>
      </NavigationMenuItem>,
    );
  }
  //TODO: Link austauschen bei Schweizer Mandaten
  if (manager.hasAbrechnungsRecht()) {
    const isActive = isMenuItemActive('abrechnnung', location.pathname);

    menuItems.push(
      <NavigationMenuItem key="payoff">
        <NavigationMenuLink active={isActive} href={buildLink('abrechnung')}>
          <FontAwesomeIcon icon={faCalculatorSimple} />
          <span className="hidden xl:inline"> {t('entities.payoff')}</span>
        </NavigationMenuLink>
      </NavigationMenuItem>,
    );
  }

  // //TODO: Nicht angzeigen bei gesperrtem Mandaten
  if (manager.rechte.dienstplan.erlaubt) {
    menuItems.push(
      createDienstplanDropdown(
        manager.rechte,
        t,
        location.pathname,
        currentStandort,
      ),
    );
  }

  if (
    manager.rechte.termine ||
    manager.rechte.mitarbeiter.dokumente ||
    manager.rechte.konflikte
  ) {
    menuItems.push(createMoreDropdown(manager.rechte, t, location.pathname));
  }
  return menuItems;
}

function createControllingDropdown(
  manager: ManagerProfile,
  t: TFunction<'translation', undefined>,
  pathname: string,
  currentStandort: Standort,
) {
  const isMenuTriggerActive = isMenuItemActive('controlling', pathname);
  let isUmsatzplanungEnabled =
    currentStandort?.featureFlags.find((flag) => flag.name === 'umsatzplanung')
      ?.isActive ?? false;
  if (currentStandort.land !== 'DE' && currentStandort.land !== 'AT') {
    isUmsatzplanungEnabled = false;
  }
  return (
    <NavigationMenuItem key="controlling">
      <NavigationMenuTrigger active={isMenuTriggerActive}>
        <FontAwesomeIcon icon={faChartMixed} />
        <span className="hidden xl:inline">{t('entities.controlling')}</span>
      </NavigationMenuTrigger>
      <NavigationMenuContent className="min-w-[200px]">
        {manager.rechte.tagebuch && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('controlling/tagebuch', pathname)}>
            <a href={buildLink('tagebuch')}>{t('entities.journal')}</a>
          </NavigationMenuContentLink>
        )}
        {manager.rechte.auswertung.dienstplan && (
          //TODO: Anderer Link wenn der Mandant Schweizer ist
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('auswertungen/dienstplan', pathname)}>
            <a href={buildLink('auswertungenDienstplan')}>
              {t('entities.analysis_other')}
            </a>
          </NavigationMenuContentLink>
        )}
        {manager.isPersonalAuswerter() && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('auswertungen/personal', pathname)}>
            <a href={buildLink('auswertungenPersonal')}>
              {t('entities.analysis_other')}
            </a>
          </NavigationMenuContentLink>
        )}
        {manager.isArbeitszeitAuswerter() && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('auswertungen/mitarbeiter', pathname)}>
            <a href={buildLink('auswertungenArbeitszeit')}>
              {t('entities.analysis_other')}
            </a>
          </NavigationMenuContentLink>
        )}
        {isUmsatzplanungEnabled && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('umsatzplanung', pathname)}>
            <Link to="/controlling/umsatzplanung">Umsatzplanung</Link>
          </NavigationMenuContentLink>
        )}
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}

function createDienstplanDropdown(
  rechte: Managerrechte,
  t: TFunction<'translation', undefined>,
  pathname: string,
  currentStandort: Standort,
) {
  const isMenuTriggerActive = isMenuItemActive('dienstplan', pathname);
  let isMindestbelegungEnabled =
    currentStandort?.featureFlags.find(
      (flag) => flag.name === 'mindestbelegung',
    )?.isActive ?? false;

  if (currentStandort.land !== 'DE' && currentStandort.land !== 'AT') {
    isMindestbelegungEnabled = false;
  }
  return (
    <NavigationMenuItem key="employeeRoster">
      <NavigationMenuTrigger active={isMenuTriggerActive}>
        <FontAwesomeIcon icon={faCalendarDays} />
        <span className="hidden xl:inline">{t('entities.roster')}</span>
      </NavigationMenuTrigger>
      <NavigationMenuContent className="min-w-[200px]">
        <NavigationMenuContentLink
          asChild
          active={isMenuItemActive('dienstplan/personal', pathname)}>
          <a href={buildLink('dienstplanPersonal')}>{t('entities.staff')}</a>
        </NavigationMenuContentLink>
        <NavigationMenuContentLink
          asChild
          active={isMenuItemActive('dienstplan/schicht', pathname)}>
          <a href={buildLink('dienstplanSchicht')}>
            {t('entities.shift_other')}
          </a>
        </NavigationMenuContentLink>
        {rechte.dienstplan.schichtvorlagen && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('dienstplan/vorlage', pathname)}>
            <a href={buildLink('dienstplanVorlage')}>
              {t('entities.template_other')}
            </a>
          </NavigationMenuContentLink>
        )}
        {isMindestbelegungEnabled && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('dienstplan/mindestbelegung', pathname)}>
            <Link to="/dienstplan/mindestbelegung">Mindestbelegung</Link>
          </NavigationMenuContentLink>
        )}
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}

function createMoreDropdown(
  rechte: Managerrechte,
  t: TFunction<'translation' | undefined>,
  pathname: string,
) {
  const isMenuTriggerActive =
    isMenuItemActive('termine', pathname) ||
    isMenuItemActive('konflikte', pathname) ||
    isMenuItemActive('dokumente', pathname) ||
    isMenuItemActive('neuigkeiten', pathname);

  return (
    <NavigationMenuItem key="more">
      <NavigationMenuTrigger active={isMenuTriggerActive}>
        <FontAwesomeIcon icon={faEllipsis} />
        <span className="hidden xl:inline">
          {t('general.navigation.moreItems')}
        </span>
      </NavigationMenuTrigger>
      <NavigationMenuContent className="min-w-[200px]">
        {rechte.konflikte && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('konflikte', pathname)}>
            <a href={buildLink('konflikte')}>{t('entities.conflict_other')}</a>
          </NavigationMenuContentLink>
        )}
        {rechte.termine && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('termine/uebersicht', pathname)}>
            <a href={buildLink('termine')}>{t('entities.event_other')}</a>
          </NavigationMenuContentLink>
        )}
        {rechte.mitarbeiter.dokumente && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('dokumente', pathname)}>
            <a href={buildLink('dokumente')}>{t('entities.document_other')}</a>
          </NavigationMenuContentLink>
        )}
        {rechte.neuigkeiten && (
          <NavigationMenuContentLink
            asChild
            active={isMenuItemActive('neuigkeiten', pathname)}>
            <a href={buildLink('neuigkeiten')}>{t('entities.neuigkeiten')}</a>
          </NavigationMenuContentLink>
        )}
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}
